'use client'
import GradientBorder, { gradientBorderRadius } from '@/app/components/gradient-border'
import { getChannelGifsById } from '@/app/util/channel-api'
import { NavigateRightIcon } from '@/app/icons/utility'
import UAParserContext from '@/context/ua-parser'
import { NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { IGif } from '@giphy/js-types'
import { GifOverlayProps, Grid } from '@giphy/react-components'
import { useRouter } from 'next/navigation'
import { useCallback, useContext, useEffect, useState } from 'react'
import useWindowSize from 'react-use/lib/useWindowSize'
import { desktopWidth } from 'ui/src/constants'
import { appleMemeMakerCopy } from './localization'

type Props = {
    initialGifs: IGif[]
    channelId: number
}

const GifSelectorGrid = ({ initialGifs, channelId }: Props) => {
    const copy = appleMemeMakerCopy.grid
    const { deviceType } = useContext(UAParserContext)
    const router = useRouter()
    const { width: windowWidth } = useWindowSize(0)
    const [hasMeasuredMobileWidth, setHasMeasuredMobileWidth] = useState(false)
    const [selectedGif, setSelectedGif] = useState<IGif>()
    const [isGridHovered, setIsGridHovered] = useState(false)
    const fetchGifs = useCallback(
        async (offset: number) => {
            return await getChannelGifsById(channelId, { offset, limit: 10 })
        },
        [channelId]
    )

    const isMobile = deviceType === 'mobile'

    useEffect(() => {
        if (isMobile && windowWidth > 0) {
            setHasMeasuredMobileWidth(true)
        }
    }, [isMobile, windowWidth])

    if (isMobile && !hasMeasuredMobileWidth) {
        return null
    }

    const SelectionOverlay = ({ gif, isHovered }: GifOverlayProps) => {
        const isSelected = `${gif.id}` === `${selectedGif?.id}`
        const shouldDim = !isMobile && isGridHovered && !isHovered && !isSelected

        return (
            <>
                <div
                    className={cn(
                        'pointer-events-none absolute inset-0 bg-black/30 opacity-0 transition-opacity duration-150 ease-out',
                        shouldDim && 'opacity-100'
                    )}
                />
                {isSelected && <GradientBorder fromColor="#FF6666" toColor="#FFF35C" />}
            </>
        )
    }

    const onNext = () => {
        if (!selectedGif) return

        router.push(`/nido-de-villanas-meme/create?gif_id=${encodeURIComponent(`${selectedGif.id}`)}`)
    }

    return (
        <div className="relative pb-[110px] md:pb-[120px]">
            <div onMouseEnter={() => setIsGridHovered(true)} onMouseLeave={() => setIsGridHovered(false)}>
                <Grid
                    initialGifs={initialGifs}
                    width={isMobile ? windowWidth : desktopWidth}
                    columns={isMobile ? 2 : 4}
                    borderRadius={gradientBorderRadius}
                    fetchGifs={fetchGifs}
                    hideAttribution
                    overlay={SelectionOverlay}
                    onGifClick={(gif, e) => {
                        e.preventDefault()
                        setSelectedGif(gif)
                    }}
                />
            </div>
            <div
                className={cn(
                    'pointer-events-none flex justify-center',
                    'fixed inset-x-0 bottom-0 z-50 px-5 pb-[max(30px,env(safe-area-inset-bottom))]'
                )}
            >
                <button
                    type="button"
                    className={cn(
                        'pointer-events-auto inline-flex min-h-[56px] min-w-[260px] items-center justify-center gap-2 rounded-full px-6 text-lg leading-none font-normal text-giphyWhite transition-all md:min-h-[70px] md:min-w-[300px] md:px-8 md:text-[22px]',
                        selectedGif
                            ? `${NexaBlack.className} cursor-pointer bg-linear-[140deg] from-giphyRed to-giphyYellow font-black shadow-[0_22px_64px_rgba(255,102,102,0.48),0_12px_26px_rgba(0,0,0,0.55)] hover:brightness-110`
                            : 'cursor-not-allowed bg-[#4E4E4E]/50 shadow-[0_22px_64px_rgba(0,0,0,0.38),0_12px_26px_rgba(0,0,0,0.55)] backdrop-blur-3xl'
                    )}
                    disabled={!selectedGif}
                    onClick={onNext}
                >
                    {selectedGif ? (
                        <>
                            {copy.next}
                            <NavigateRightIcon className="h-3 w-1.5 md:h-3.5 md:w-2" />
                        </>
                    ) : (
                        copy.chooseGifToBegin
                    )}
                </button>
            </div>
        </div>
    )
}

export default GifSelectorGrid
